import type { DrawerProps } from '@mui/material/Drawer';
import { actionHelpers } from 'app-components/flux/core';
import { customersActions, customersSelectors } from 'app-components/flux/customers';
import type { Customer, CustomerProps } from 'app-components/flux/customers/customersTypes';
import { tracker } from 'app-components/utils/core';
import type { SubmitHandler } from 'app-components/utils/createForm/formBuilder';
import { CREATE_SEARCH_PARAM } from 'containers/Customers/utils';
import { useAppStore } from 'hooks/redux';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { v4 as uuid } from 'uuid';
import InnerDrawer, { FormProvider } from './CustomerFields';

type AddCustomerDrawerProps = DrawerProps & {
  onUpsert?: (customer: Customer) => void;
};

export default function AddCustomerDrawer(props: AddCustomerDrawerProps) {
  const { onUpsert, ...other } = props;
  const [searchParams, setSearchParams] = useSearchParams();
  const store = useAppStore();

  const searchParam = searchParams.get('editCustomer');
  const initBusinessName = searchParams.get('businessName');

  const customerId = searchParam !== CREATE_SEARCH_PARAM ? searchParam : null;
  const open = Boolean(searchParam);
  useEffect(() => {
    if (searchParam === CREATE_SEARCH_PARAM) {
      tracker.track(tracker.events.createClientStart);
    }
  }, [searchParam]);

  const onClose = useCallback(() => {
    searchParams.delete('editCustomer');
    searchParams.delete('businessName');
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const input = useMemo(() => {
    // Store directly as input does not need to be reactive
    const customer = customerId ? customersSelectors.getCustomerById(store.getState(), customerId) : undefined;
    return (customer?.toJS() as Partial<CustomerProps>) ?? { businessName: initBusinessName };
  }, [customerId, initBusinessName, store]);

  const dispatch = useDispatch();

  const onSubmit: SubmitHandler<Customer> = useCallback(
    async ({ payload }) => {
      // create new client
      if (!payload.id) {
        const createCustomerAction = actionHelpers.bindPromiseAction(dispatch, customersActions.createCustomer);
        await createCustomerAction(payload.set('clientId', uuid())).then((response: any) => {
          tracker.track(tracker.events.createClientSaved);
          if (onUpsert) {
            onUpsert(response.resource);
          }
        });
      } else {
        // edit client
        tracker.track(tracker.events.editClient);
        dispatch(customersActions.updateCustomer(payload));
      }
      onClose();
    },
    [dispatch, onUpsert, onClose],
  );

  if (!open) {
    return null;
  }

  return (
    <FormProvider input={input} onSubmit={onSubmit}>
      <InnerDrawer {...other} onClose={onClose} />
    </FormProvider>
  );
}
